import {
    ConvertedGeometry, ConvertedGeometryByStage, GeometryByStage,
    ProjectsResponse, ProjectStatus, SelectedFeature, StageName, StageType,
    STATUS_PRIORITY_CONTRACTOR,
    STATUS_PRIORITY_DEPARTMENT_SPECIALIST, TerritoryType,
    TransformedProjectsData, ValidationIssue, ValidationResult
} from "../interfaces/utilities";
import {formatTableTime} from "./format-table-dates";
import Graphic from "@arcgis/core/Graphic";
import {LayersDataTypes} from "../types/layers-data-types";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Polygon from "@arcgis/core/geometry/Polygon";
import {Layer} from "../interfaces/layer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import {getStageKey, stages} from "../services/reducers/utilities-journal";

export const defaultPolygonSymbol = new SimpleFillSymbol({
    color: '#2E1A471A',
    outline: {
        color: '#2E1A47',
        width: 2
    }
});

export const defaultPolylineSymbol = new SimpleLineSymbol({
    color: '#2E1A47',
    width: 2
});

export const formatProject = (projectData: ProjectsResponse, isContractor: boolean): TransformedProjectsData => {
    const dateTimeFormat = "MM/DD/YYYY hh:mm AM";
    const dateOnlyFormat = "MM/DD/YYYY";

    const formattedModificationDate = projectData.modificationDate
        ? formatTableTime(new Date(projectData.modificationDate), dateTimeFormat)
        : "";
    const formattedIssueDate = projectData.workPermitIssueDate
        ? formatTableTime(new Date(projectData.workPermitIssueDate), dateOnlyFormat)
        : "";
    const formattedExpireDate = projectData.workPermitExpireDate
        ? formatTableTime(new Date(projectData.workPermitExpireDate), dateOnlyFormat)
        : "";
    const formattedBuildingPermitIssueDate = projectData.buildingPermitIssueDate ?
        formatTableTime(new Date(projectData.buildingPermitIssueDate), dateOnlyFormat)
        : ""
    const formattedBuildingPermitExpireDate = projectData.buildingPermitExpireDate ?
        formatTableTime(new Date(projectData.buildingPermitExpireDate), dateOnlyFormat)
        : ""

    const toTitleCase = (str: string): string =>
        str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

    let rawStage = toTitleCase(projectData.stageName ?? "Design Stage") as string;

    let stage: StageName = rawStage === "Design Stage" ? "Design Stage" :
        rawStage === "As-Built" ? "As-Built" :
            "Design Stage";

    let status = isContractor
        ? (projectData.statusNameForContractor ?? ProjectStatus.NotInitiated)
        : (projectData.statusNameForMadayn ?? ProjectStatus.NotInitiated);

    if (stage === "Design Stage" && status === "Approved") {
        stage = "As-Built"
        status = ProjectStatus.NotInitiated
    }

    const statusAndModificationDate = `${status}\n${formattedModificationDate}`;

    const contractorAndDescriptionOfWork = `${projectData.contractorOrgName ?? ""}\n${projectData.workPermitDescription ?? ""}`;


    const workPermitStatus = `${projectData.workPermitStage}${
        projectData.buildingPermitNumber ? `\nBuilding permit is ${projectData.buildingPermitStage}` : ""
    }`;

    const duration = `${formattedIssueDate}\n${formattedExpireDate}`;

    return {
        id: projectData.workPermitId,
        statusAndModificationDate,
        contractorAndDescriptionOfWork,
        workPermitStatus,
        duration,
        stage,
        workPermitNumber: projectData.workPermitNumber ?? '',
        notificationId: projectData.notificationId,
        status,
        issueDate: formattedIssueDate,
        expireDate: formattedExpireDate,
        modificationDate: formattedModificationDate,
        descriptionOfWork: projectData.workPermitDescription ?? '',
        contractor: projectData.contractorOrgName ?? '',
        buildingPermitIssueDate: formattedBuildingPermitIssueDate,
        buildingPermitExpireDate: formattedBuildingPermitExpireDate,
        buildingPermitStage: projectData.buildingPermitStage,
        buildingPermitNumber: projectData.buildingPermitNumber,
        workPermitEstateId: projectData.workPermitEstateId,
        workPermitId: projectData.workPermitId,
        workPermitEstate: projectData.workPermitEstate,
        projectId: projectData.projectId,
        workPermitDatePayment: projectData.workPermitDatePayment,
        depositPayed: projectData.depositPayed,
        stageId: projectData.stageId,
        workPermitStage: projectData.workPermitStage,
        contractorOrgName: projectData.contractorOrgName,
    };
};

export const sortProjects = (
    projectData: TransformedProjectsData[],
    isContractor: boolean,
) => {
    return [...projectData].sort((first, second) => {
        const todayDate = new Date();

        const statusPriority: Record<string, number> = isContractor
            ? STATUS_PRIORITY_CONTRACTOR
            : STATUS_PRIORITY_DEPARTMENT_SPECIALIST;

        const firstStatusRank: number = statusPriority[first.status] ?? -1;
        const secondStatusRank: number = statusPriority[second.status] ?? -1;
        if (firstStatusRank !== secondStatusRank) return firstStatusRank - secondStatusRank;

        const firstHasModDate: boolean = !!first.modificationDate;
        const secondHasModDate: boolean = !!second.modificationDate;

        if (firstHasModDate !== secondHasModDate) {
            return firstHasModDate ? -1 : 1;
        }

        if (firstHasModDate && secondHasModDate) {
            const firstModDate = new Date(first.modificationDate!);
            const secondModDate = new Date(second.modificationDate!);
            const modDateDiff = secondModDate.getTime() - firstModDate.getTime();
            if (modDateDiff !== 0) return modDateDiff;

            const firstExpire = new Date(first.expireDate);
            const secondExpire = new Date(second.expireDate);
            return secondExpire.getTime() - firstExpire.getTime();
        }

        const firstExpire = first.expireDate ? new Date(first.expireDate) : null;
        const secondExpire = second.expireDate ? new Date(second.expireDate) : null;

        const isFirstFuture = firstExpire && firstExpire > todayDate;
        const isSecondFuture = secondExpire && secondExpire > todayDate;

        if (isFirstFuture && isSecondFuture) {
            return firstExpire.getTime() - secondExpire.getTime();
        } else if (!isFirstFuture && !isSecondFuture) {
            return secondExpire!.getTime() - firstExpire!.getTime();
        } else {
            return isFirstFuture ? -1 : 1;
        }
    });
}

export const validateConfirmStageTerritory = async (
    layers: Layer[],
    graphic: Graphic,
    selectedRow: TransformedProjectsData
) => {
    if (!graphic || !selectedRow) return
    const filteredCityLayers = layers.filter(
        layer => layer.dataType === LayersDataTypes.CityBoundary
    );

    const featureLayers = filteredCityLayers
        .filter(layer => !!layer.url)
        .map(layer => new FeatureLayer({
            url: layer.url,
            outFields: ['*']
        }));

    for (const layer of featureLayers) {
        await layer.load();

        const query = layer.createQuery();
        query.geometry = graphic.geometry;
        query.spatialRelationship = "intersects";
        query.returnGeometry = false;

        let intersectsCity = false;
        let attributeMatch = false;

        const result = await layer.queryFeatures(query);

        if (result.features.length > 0) {
            intersectsCity = true;

            for (const feature of result.features) {
                const estateEn = feature.attributes?.estateEn;
                if (estateEn === selectedRow.workPermitEstateId) {
                    attributeMatch = true;
                    break;
                }
            }
        }

        if (intersectsCity && attributeMatch) {
            return true;
        }
    }

    return false;
};

export const isGeometryContained = (
    outer: Graphic | null,
    inner: Graphic | null
): boolean => {
    if (!outer || !inner) return false;

    return geometryEngine.contains(outer.geometry, inner.geometry);
};

const getValidGeometry = (geometry: any): Geometry | null => {
    const parsed = typeof geometry === 'string' ? JSON.parse(geometry) : geometry;

    if (parsed.type) {
        switch (parsed.type) {
            case "point":
                return new Point(parsed);
            case "polyline":
                return new Polyline(parsed);
            case "polygon":
                return new Polygon(parsed);
        }
    }

    if ("x" in parsed && "y" in parsed) {
        return new Point(parsed);
    }

    if ("paths" in parsed) {
        return new Polyline(parsed);
    }

    if ("rings" in parsed) {
        return new Polygon(parsed);
    }

    return null;
};

export const validateConvertedGeometry = (convertedGeometry: ConvertedGeometry): ValidationResult => {
    const issues: ValidationIssue[] = [];

    for (const [layerName, layerData] of Object.entries(convertedGeometry)) {
        let hasError = false;

        for (const graphic of layerData.graphics) {
            if (hasError) break;

            const geometry = graphic.geometry;

            const validGeometry = getValidGeometry(geometry)

            if (!validGeometry) {
                issues.push({ layerName, reason: 'Invalid Geometry' });
                hasError = true;
                break;
            }

            if (!geometryEngine.isSimple(validGeometry)) {
                issues.push({ layerName, reason: 'Invalid Geometry' });
                hasError = true;
                break;
            }

            if (geometry instanceof Polyline) {
                const seen = new Set<string>();
                for (const path of geometry.paths) {
                    for (const [x, y] of path) {
                        const key = `${x}_${y}`;
                        if (seen.has(key)) {
                            issues.push({ layerName, reason: 'Invalid Geometry' });
                            hasError = true;
                            break;
                        }
                        seen.add(key);
                    }
                    if (hasError) break;
                }
            }

            if (geometry instanceof Polygon) {
                const rings = geometry.rings;

                for (const [index, ring] of rings.entries()) {
                    const seen = new Set<string>();
                    for (const [x, y] of ring) {
                        const key = `${x}_${y}`;
                        if (seen.has(key)) {
                            issues.push({
                                layerName,
                                reason: 'Invalid Geometry'
                            });
                            hasError = true;
                            break;
                        }
                        seen.add(key);
                    }
                    if (hasError) break;
                }

                if (!hasError && rings.length > 1) {
                    const contours = rings.map(ring =>
                        new Set(ring.map(([x, y]) => `${x}_${y}`))
                    );
                    outerContours:
                        for (let i = 0; i < contours.length; i++) {
                            for (let j = i + 1; j < contours.length; j++) {
                                for (const point of contours[i]) {
                                    if (contours[j].has(point)) {
                                        issues.push({ layerName, reason: 'Invalid Geometry' });
                                        hasError = true;
                                        break outerContours;
                                    }
                                }
                            }
                        }
                }
            }
        }
    }

    return {
        isValid: issues.length === 0,
        issues
    };
};

export const hasValidBuildingPermit = (
    selectedRow?: TransformedProjectsData | null
): boolean => {
    if (!selectedRow) return false;
    return Boolean(
        selectedRow.buildingPermitNumber &&
        selectedRow.buildingPermitExpireDate &&
        new Date(selectedRow.buildingPermitExpireDate).getTime() >= Date.now(),
    );
};

export const hasValidBuildingPermitForStage = (
  selectedRow?: TransformedProjectsData | null,
  stageType?: StageType,
): boolean => {
  if (!selectedRow || !stageType) return false;
  return (
    getStageKey(selectedRow.stage) === stageType &&
    hasValidBuildingPermit(selectedRow)
  );
};

export const getMapLayerKey = (stage: StageType, fileName: string): string => {
    return `${stage}|${fileName}`;
};

export const getCesiumLayerKey = (stage: StageType, fileName: string) => {
    return `tmp_${getMapLayerKey(stage, fileName)}`
};

export const getTerritoryLayerName = (type: TerritoryType, stage: StageType): string => {
    return `tmp_territory_${type}_${stage}`;
}

export const isProtectedGraphic = (
    selectedFeature: SelectedFeature,
    stageTerritoryGeometry: GeometryByStage,
    buildingTerritoryGeometry: GeometryByStage,
    selectedRow: TransformedProjectsData,
    convertedGeometry: ConvertedGeometryByStage,
) => {
    if (!selectedRow) return;

    const selectedId = selectedFeature?.attributes?.uuid;
    const currentStageKey = getStageKey(selectedRow.stage);

    if (!selectedId) return false;

    const isSameId = (g: Graphic | null) => {
        if (!g || !g.attributes) return false;
        const id = g.attributes.uuid;
        return id === selectedId;
    };

    for (const stage of stages) {
        if (isSameId(stageTerritoryGeometry[stage]) || isSameId(buildingTerritoryGeometry[stage])) {
            return true;
        }
    }

    for (const stage of stages) {
        if (stage === currentStageKey) continue;

        const layers = convertedGeometry[stage];
        if (!layers) continue;

        for (const layer of Object.values(layers)) {
            if (layer.graphics.some(g => g.attributes?.uuid === selectedId)) {
                return true;
            }
        }
    }

    return false;
}